import { cn } from "@/app/utils/cn";
import { ChevronRight } from "lucide-react";
import { PartnerType } from "@/app/utils/partners.enum";
import { Skeleton } from "@/view/components/ui/skeleton";

interface IPartnerType {
  title: string;
  bgColor: string;
  imageSrc: string;
  description: string;
  needAuthorization: boolean;
  submit: () => void;
  type: string;
  disabledButton?: boolean;
  isLoading?: boolean;
}

const autocontratacaoImageMap: Partial<Record<PartnerType, string>> = {
  [PartnerType.CLT]: "/clt_logo_autocontratacao.png",
  [PartnerType.PAN]: "/pan_logo_autocontratacao.png",
};

const getAutocontratacaoImage = (type: string, fallback: string): string => {
  return autocontratacaoImageMap[type as PartnerType] ?? fallback;
};

const enlargedImageTypes = new Set<string>([
  PartnerType.FGTS,
  PartnerType.PAN,
  PartnerType.CREFAZ,
]);

export const PartnerCard = ({
  isLoading,
  title,
  bgColor,
  imageSrc,
  description,
  submit,
  type,
  disabledButton = false,
}: IPartnerType) => {
  const resolvedImage = getAutocontratacaoImage(type, imageSrc);

  if (isLoading) {
    return (
      <div
        aria-busy="true"
        aria-live="polite"
        className="flex w-full animate-pulse flex-col items-stretch gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:items-center sm:gap-4"
      >
        <div
          className="relative flex h-20 w-full shrink-0 items-center justify-center overflow-hidden rounded-lg sm:w-32"
          style={{ backgroundColor: bgColor }}
        >
          <img
            src={resolvedImage}
            alt={type}
            className={cn(
              "object-contain opacity-60",
              enlargedImageTypes.has(type)
                ? "h-20 max-w-[100%]"
                : "h-12 max-w-[80%]",
            )}
          />
        </div>
        <div className="flex flex-1 items-center gap-3">
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
          <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={submit}
      disabled={disabledButton}
      className={cn(
        "flex w-full flex-col items-stretch gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-gray-300 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 sm:flex-row sm:items-center sm:gap-4",
      )}
    >
      <div
        className="flex h-20 w-full shrink-0 items-center justify-center rounded-lg sm:w-32"
        style={{ backgroundColor: bgColor }}
      >
        <img
          src={resolvedImage}
          alt={type}
          className={cn(
            "object-contain",
            enlargedImageTypes.has(type)
              ? "h-20 max-w-[100%]"
              : "h-12 max-w-[80%]",
          )}
        />
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-sm font-semibold text-black dark:text-white">
            {title}
          </p>
          <p className="text-xs leading-snug text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </div>

        <ChevronRight
          size={20}
          className="shrink-0 text-gray-400 dark:text-gray-300"
        />
      </div>
    </button>
  );
};
